import {
  FeatureFlagValues,
  FeatureGroupDefinition
} from './platform/feature-management/feature-management';
import { FeatureCleanupVersionMap } from './platform/feature-management/sources/localStorage';

/**
 * Definition of all Atellio features and their purpose, despite their origin (server or frontend).
 */
export const featureFlags = {
  groups: [
    {
      name: 'Business',
      entries: [
        {
          id: 'new-measurement-field',
          name: 'Measurement Field',
          description:
            'Measurement field with support for imperial and metric units',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'calendar',
          name: 'Calendar',
          description: 'Visualize your creative schedules effortlessly',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'resources',
          name: 'Resources',
          description: 'Efficiently allocate studios, equipment, and more',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'productions',
          name: 'Productions',
          description: 'Coordinate your creative productions with clarity',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'shortlists',
          name: 'Shortlists',
          description: 'Curate your top talent picks seamlessly',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'callouts',
          name: 'Callouts',
          description: 'Broadcast project briefs to talent instantly',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'callsheets',
          name: 'Callsheets',
          description: 'Craft beautiful, detailed callsheets in minutes',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'bookings',
          name: 'Bookings',
          description: 'Manage bookings with simplicity and precision',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'budgeting',
          name: 'Budgeting',
          description: 'Track and control creative project budgets',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'financials',
          name: 'Financials',
          description: 'Insightful financial tracking and reporting',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'reporting',
          name: 'Reporting',
          description: 'Powerful analytics for your creative workflow',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'reconciliations',
          name: 'Reconciliations',
          description: 'Effortlessly reconcile invoices and expenses',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'custom-nikefrontdesk',
          name: 'Front Desk',
          description: 'Tailored front desk experience',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'custom-nikearrivalboard',
          name: 'Arrival Board',
          description: 'Interactive arrival boards',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'custom-nikeqrcode',
          name: 'QR Code',
          description: 'Simplified check-ins with QR codes',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'booking_confirmation',
          name: 'Booking Confirmation',
          description: 'Instant, professional booking confirmations',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'tasks',
          name: 'Tasks',
          description: 'Assign and track creative tasks effortlessly',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'custom-splashlight',
          name: 'Netsuite Integration',
          description: 'Custom integration into Netsuite',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'custom-shotflow',
          name: 'Shotflow Integration',
          description: 'Custom integration into Shotflow',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        }
      ]
    },
    {
      name: 'Labs',
      entries: [
        {
          id: 'enterprise',
          name: 'Enterprise',
          description:
            'Organization, workspace, and team navigation with enterprise routes and sidebars',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          },
          shortcut: 'ctrl+shift+e'
        },
        {
          id: 'manage-enterprise',
          name: 'Manage enterprise',
          description:
            'Full-page UI to create, edit, and archive enterprise workspaces and teams',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'resource-pane',
          name: 'Resource Pane',
          description:
            'New resource pane uses improved data management which solves data stickiness issues',
          flagSpec: {
            type: 'enum',
            options: ['new', 'v6'],
            defaultValue: 'v6'
          },
          shortcut: 'ctrl+shift+r'
        },
        {
          id: 'ported-v1-endpoints',
          name: 'Ported v1 endpoints',
          description:
            'Send the calls that used to go to the v2 service to v1 instead, where they now also run',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'booking-statistics',
          name: 'Booking Statistics',
          description: 'Resource booking statistics panel',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'new-production-resourcing',
          name: 'New Production Resourcing',
          description:
            'Modern v5 production resourcing table with optimistic UI',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          },
          shortcut: 'ctrl+shift+p'
        },
        {
          id: 'new-production-resourcing-version-switcher',
          name: 'New Production Resourcing Version Switcher',
          description:
            'Show the classic / new version toggle banner on production resourcing',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'resourcing-online-sync-toggle',
          name: 'Resourcing online sync toggle',
          description:
            'Show the offline/online sync pause control in the new production resourcing footer',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'low-level-validation',
          name: 'Low-level validation UI',
          description:
            'When enabled, invalid list/grid rows use the advanced validation UI (banner row, manual preview). When disabled, rows use the simpler auto-preview with a corner warning icon.',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'search-from-source',
          name: 'Search From Source',
          description:
            'Backend read-path toggle: serve search from the live source tables instead of the search-index tables',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'non-n-plus-1-productions',
          name: 'Faster productions search',
          description:
            'Backend hydration: serve productions search via a single aggregated query instead of N+1 relation loads',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'non-n-plus-1-contacts',
          name: 'Faster contacts search',
          description:
            'Backend hydration: serve contacts search via a single aggregated query instead of N+1 relation loads',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'non-n-plus-1-callsheets',
          name: 'Faster callsheets search',
          description:
            'Backend hydration: serve callsheets search via a single aggregated query instead of N+1 relation loads',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'request-availability-v5',
          name: 'Request Availability v5',
          description:
            'New Request Availability dialog with Via Email / Via Link tabs (v5). When off, use the legacy modal.',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'content-theme',
          name: 'Content theme',
          description:
            'Adds a Content theme section to the theme menu: light, dark or system for the page content, apart from the sidebar',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        },
        {
          id: 'shortlist-v5',
          name: 'Shortlists v5',
          description:
            'Shortlists v5 in both views: the team’s shortlist pages (one board for a thousand people, the Add resources panel with gallery and profile, shortlist settings, Create shortlist) and the public page clients open by link. On for a workspace, it switches that workspace’s shortlists; on for the organisation, the shortlists outside any workspace.',
          flagSpec: {
            type: 'boolean',
            defaultValue: false
          }
        }
      ]
    },
    {
      name: 'Devtools',
      entries: [
        {
          id: 'dev-origin',
          name: 'Dev server branch in the sidebar',
          description:
            'Shows the branch and folder the local vite dev server runs from at the foot of the sidebar, updated as soon as the branch changes. Tells apart several local apps open at once; shows nothing outside `npm run dev`.',
          flagSpec: {
            type: 'boolean',
            // on by itself where we develop: each localhost port keeps its own flags, so switching
            // it on by hand would have to be repeated for every worktree's port
            defaultValue: import.meta.env.DEV
          }
        }
      ]
    }
  ]
} as const satisfies FeatureGroupDefinition;

/**
 * Extra mapping for localStorage cleanup.
 * The value is a cleanup ID that will be checked against user's local storage saved feature flag value.
 * If the value is different, the localStorage will be cleaned up.
 * That way we can reset user's choice to use specific version of a feature.
 */
export const localStorageCleanupVersionMapping: FeatureCleanupVersionMap<AtellioFeatureGroupDefinition> =
  {
    'resource-pane': '4',
    'new-measurement-field': '1',
    'booking-statistics': '1',
    'ported-v1-endpoints': '1',
    enterprise: '1',
    'new-production-resourcing': '1',
    'new-production-resourcing-version-switcher': '1',
    'resourcing-online-sync-toggle': '1',
    'low-level-validation': '1',
    'manage-enterprise': '1',
    'request-availability-v5': '1',
    'dev-origin': '1',
    'content-theme': '1',
    'shortlist-v5': '1'
  };

export const forcedFeatureFlags: Partial<
  FeatureFlagValues<AtellioFeatureGroupDefinition>
> = {
  'new-production-resourcing': true,
  'new-production-resourcing-version-switcher': false
};

export type AtellioFeatureGroupDefinition = typeof featureFlags;
